import { memo, useId, useMemo, useState } from "react";
import Badge, { convictionVariant } from "../ui/Badge";
import { PILLARS, Pillar } from "./stock-card-sections";
import styles from "./stock-card.module.css";

// ── Formatters ───────────────────────────────────────────────────────────
const fmt = {
  usd: (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`),
  big: (v) => {
    if (v == null) return "—";
    const abs = Math.abs(v);
    const sign = v < 0 ? "-" : "";
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    return `${sign}$${abs.toLocaleString()}`;
  },
  int: (v) => (v == null ? "—" : Number(v).toLocaleString()),
};

// ── Token helpers (the only runtime-computed colors allowed) ─────────────
const TONE = {
  positive: { color: "var(--positive)", soft: "var(--positive-soft)", border: "var(--accent-border)" },
  neutral:  { color: "var(--neutral)",  soft: "var(--neutral-soft)",  border: "rgba(251,191,36,0.28)" },
  negative: { color: "var(--negative)", soft: "var(--negative-soft)", border: "rgba(248,113,113,0.28)" },
};
const scoreTone = (s) => (s == null ? "neutral" : s >= 7.5 ? "positive" : s >= 6 ? "neutral" : "negative");

// ── Score ring (≥18px value per spec) ───────────────────────────────────
const ScoreRing = ({ score }) => {
  const size = 56;
  const stroke = 4;
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.min(Math.max(score / 10, 0), 1);
  const tone = TONE[scoreTone(score)];
  return (
    <div
      className={styles.scoreRing}
      role="img"
      aria-label={score == null
        ? "Quality score unavailable"
        : `Quality score: ${Number(score).toFixed(1)} out of 10`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={tone.color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct)}
        />
      </svg>
      <span className={styles.scoreRingValue} style={{ color: tone.color }}>
        {score == null ? "—" : Number(score).toFixed(1)}
      </span>
    </div>
  );
};

const MetricTile = ({ label, value, tone }) => {
  const color = tone ? TONE[tone].color : "var(--text-primary)";
  return (
    <div className={styles.metricTile}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValueMd} style={{ color }}>{value}</span>
    </div>
  );
};

// ── Card ────────────────────────────────────────────────────────────────
// Shows financials + rating only: header KPIs (quality score, conviction,
// fair value gap), valuation, financial health, and the deterministic
// quality breakdown. Narrative sections (conviction reasoning, about,
// beatdown, news) were deliberately removed - the chat agent is the
// place for that kind of analysis.
const StockCard = ({ stock: rawStock, open = false }) => {
  const stock = rawStock?.stock || rawStock || {};
  const report = rawStock?.analyst_report || null;
  const hasReport = !!report;

  const qual = report?.qualitative || {};
  const score = qual.weighted_score;
  const conviction = report?.conviction;
  const upsideRaw = stock.upside_pct;
  const upsideNum = Number(upsideRaw) || 0;
  const upsidePos = upsideNum >= 0;
  const upsideLabel = useMemo(() => {
    if (upsideRaw == null) return "—";
    if (upsideNum > 0.05) return "Undervalued";
    if (upsideNum < -0.05) return "Overvalued";
    return "At fair value";
  }, [upsideRaw, upsideNum]);

  const [expanded, setExpanded] = useState(open);
  const bodyId = useId();
  const titleId = useId();

  if (!rawStock) return null;

  const sectorLine = [stock.sector, stock.industry].filter(Boolean).join(" · ");
  const upsideTone = TONE[upsidePos ? "positive" : "negative"];
  const netCashPos = (stock.net_cash || 0) >= 0;
  const ivAboveMarket =
    stock.average_intrinsic_value != null &&
    stock.current_price != null &&
    (stock.average_intrinsic_value - stock.current_price) / stock.current_price > 0.05;

  return (
    <article className={styles.root} aria-labelledby={titleId}>
      <header className={styles.header}>
        <div className={styles.identity}>
          <span id={titleId} className={styles.ticker}>{stock.ticker}</span>
          {stock.company_name && (
            <span className={styles.companyName}>{stock.company_name}</span>
          )}
          {sectorLine && <span className={styles.sectorLine}>{sectorLine}</span>}
          {!hasReport && (
            <span className={styles.headerMeta}>
              <span className={styles.limitedBadge}>Limited data</span>
            </span>
          )}
        </div>

        <div className={styles.kpiRow}>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Quality</span>
            <div className={styles.kpiValueRow}>
              {score != null ? <ScoreRing score={score} /> : <span className={styles.kpiEmpty}>—</span>}
              {score != null && <span className={styles.kpiSub}>/ 10</span>}
            </div>
          </div>

          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Conviction</span>
            <div className={styles.kpiValueRow}>
              {conviction
                ? <Badge variant={convictionVariant(conviction)}>{conviction}</Badge>
                : <span className={styles.kpiEmpty}>—</span>}
            </div>
          </div>

          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Fair Value Gap</span>
            <div className={styles.kpiValueRow}>
              {upsideRaw != null ? (
                <span className={styles.kpiValueLg} style={{ color: upsideTone.color }}>
                  {upsidePos ? "▲" : "▼"} {Math.abs(upsideNum).toFixed(1)}%
                </span>
              ) : (
                <span className={styles.kpiEmpty}>—</span>
              )}
            </div>
            {upsideRaw != null && <span className={styles.kpiSub}>{upsideLabel}</span>}
          </div>
        </div>
      </header>

      <button
        type="button"
        className={styles.expandBtn}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? "Hide details" : "Show details"}
        <svg
          className={`${styles.expandChevron}${expanded ? " " + styles.expandChevronOpen : ""}`}
          width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
        >
          <path d="M2.5 4l3.5 3.5L9.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div
        id={bodyId}
        className={`${styles.expander}${expanded ? " " + styles.expanderOpen : ""}`}
        aria-hidden={!expanded}
      >
        <div className={styles.expanderInner}>
          <div className={styles.body}>
            {(stock.current_price != null || stock.average_intrinsic_value != null) && (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Valuation</span>
                <div className={styles.metricGrid4}>
                  <MetricTile label="Price" value={fmt.usd(stock.current_price)} />
                  <MetricTile
                    label="Fair Value"
                    value={fmt.usd(stock.average_intrinsic_value)}
                    tone={ivAboveMarket ? "positive" : undefined}
                  />
                  <MetricTile label="DCF" value={fmt.usd(stock.dcf_intrinsic_value)} />
                  <MetricTile label="EPV" value={fmt.usd(stock.epv_intrinsic_value)} />
                </div>
              </div>
            )}

            {(stock.free_cash_flow != null || stock.cash != null || stock.debt != null || stock.net_cash != null) && (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Financial health</span>
                <div className={styles.metricGrid4}>
                  <MetricTile label="Free Cash Flow" value={fmt.big(stock.free_cash_flow)} tone="positive" />
                  <MetricTile label="Cash" value={fmt.big(stock.cash)} />
                  <MetricTile label="Debt" value={fmt.big(stock.debt)} tone="negative" />
                  <MetricTile
                    label="Net Cash"
                    value={fmt.big(stock.net_cash)}
                    tone={netCashPos ? "positive" : "negative"}
                  />
                </div>
                <div className={styles.metricGrid2}>
                  <MetricTile label="Market Cap" value={fmt.big(stock.market_cap)} />
                  <MetricTile label="Employees" value={fmt.int(stock.employees)} />
                </div>
              </div>
            )}

            {hasReport && (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Quality breakdown</span>
                <div className={styles.pillarList}>
                  {PILLARS.map((p) => <Pillar key={p.id} pillar={p} qual={qual} />)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

// Only re-render when ticker, key price/upside, or the report identity changes.
const areEqual = (prev, next) => {
  if (prev.open !== next.open) return false;
  const ps = prev.stock?.stock || prev.stock;
  const ns = next.stock?.stock || next.stock;
  if (ps?.ticker !== ns?.ticker) return false;
  if (ps?.upside_pct !== ns?.upside_pct) return false;
  if (ps?.current_price !== ns?.current_price) return false;
  if (prev.stock?.analyst_report !== next.stock?.analyst_report) return false;
  return true;
};

export default memo(StockCard, areEqual);
